import { useAtomValue } from '@effect/atom-react';
import {
  type InstantFlagName,
  readInstantClientFlag,
} from '@listening/api-contract/flags';
import { useSavedClientFlags } from '@listening/frontend/hooks/use-saved-client-flags';
import { idleQuery } from '@listening/frontend/query/api-runtime';
import { flagsAtom } from '@listening/frontend/query/flags';
import { AsyncResult } from 'effect/reactivity';

import { clientEnv } from '@/env';
import { useAuthSession } from '@/services/auth-service';

/**
 * The viewer's flags: the fresh response once the session settles, else the
 * values this browser saved for the viewer, plus its signed-out values.
 */
function useFlagValues() {
  const { user, isPending } = useAuthSession();
  const userId = isPending ? undefined : (user?.userId ?? null);
  const fresh = AsyncResult.getOrElse(
    useAtomValue(userId === undefined ? idleQuery : flagsAtom(userId)),
    () => undefined,
  );
  return {
    view: useSavedClientFlags({ userId, fresh, stage: clientEnv.mode }),
    loaded: fresh !== undefined,
  };
}

/** The viewer's value, else the flag's `whileUnknown` fallback. */
export function useFeatureFlag<N extends InstantFlagName<'web'>>(name: N) {
  return readInstantClientFlag(name, useFlagValues().view);
}

export function useFeatureFlagsLoaded() {
  return useFlagValues().loaded;
}
